import { produce } from 'immer';
import { tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { UnitType } from '@mitsubishi/shared/data-access';

import { Action, NgxsOnInit, State, StateContext } from '@ngxs/store';

import { News, NewsFilters, NewsStatuses, NewsTag } from '../../models';
import { NewsService } from '../../services/news.service';
import { NewsActions } from '../actions/news.actions';
import { NewsAdminStateModel } from './news-admin.state';

export interface NewsStateModel {
  list: News[];
  statuses: NewsStatuses[];
  tags: NewsTag[];
  filters: {
    searchText: string | null;
    tags: string[];
    status: string | null;
    type: number | null;
    unitId: string | null;
    unitType: UnitType | null;
  };
  sorting: {
    title: boolean;
    date: boolean;
  };
}

@State<NewsStateModel>({
  name: 'news',
  defaults: {
    list: [],
    statuses: [],
    tags: [],
    filters: {
      searchText: null,
      tags: [],
      status: null,
      type: 0,
      unitId: null,
      unitType: null,
    },
    sorting: {
      title: true,
      date: false,
    },
  },
})
@Injectable({
  providedIn: 'root',
})
export class NewsState implements NgxsOnInit {
  constructor(private newsService: NewsService) {}

  ngxsOnInit(ctx: StateContext<NewsStateModel>) {
    ctx.dispatch(NewsActions.LoadTags);
  }

  @Action(NewsActions.Load)
  public load({ patchState, getState }: StateContext<NewsStateModel>) {
    const state = getState();
    const req: NewsFilters = {
      title: state.filters.searchText,
      status: state.filters.status,
      tagIds: state.filters.tags,
      unitId: state.filters.unitId,
      unitType: state.filters.unitType,
    };
    return this.newsService.getNewsWithFilters(req).pipe(
      tap(result => {
        patchState({
          list: result,
        });
      })
    );
  }

  @Action(NewsActions.SetNewsType)
  public setNewsAdminType(ctx: StateContext<NewsStateModel>, { type }: NewsActions.SetNewsType) {
    ctx.patchState(
      produce(ctx.getState(), state => {
        state.filters.type = type;
      })
    );

    return ctx.dispatch(NewsActions.ResetNewsFilters);
  }

  @Action(NewsActions.SetSearchText)
  public setSearchText(ctx: StateContext<NewsStateModel>, { searchText }: NewsActions.SetSearchText) {
    ctx.patchState(
      produce(ctx.getState(), state => {
        state.filters.searchText = searchText;
      })
    );

    return ctx.dispatch(new NewsActions.Load());
  }

  @Action(NewsActions.LoadTags)
  public loadTags({ patchState }: StateContext<NewsStateModel>) {
    return this.newsService.getNewsTags().pipe(
      tap(result => {
        patchState({
          tags: result,
        });
      })
    );
  }

  @Action(NewsActions.ResetNewsFilters)
  public resetNewsFilters(ctx: StateContext<NewsStateModel>) {
    ctx.patchState({
      filters: {
        searchText: null,
        tags: [],
        status: null,
        type: ctx.getState().filters.type,
        unitId: null,
        unitType: null,
      },
    });

    return ctx.dispatch(new NewsActions.Load());
  }

  @Action(NewsActions.SetUnitFilter)
  public setUnitFilters(ctx: StateContext<NewsStateModel>, { unitId, unitType }: NewsActions.SetUnitFilter) {
    ctx.patchState(
      produce(ctx.getState(), state => {
        state.filters.unitId = unitId;
        state.filters.unitType = unitType;
      })
    );
  }
  @Action(NewsActions.SetSearchTags)
  public setSearchTags(ctx: StateContext<NewsAdminStateModel>, { tags }: NewsActions.SetSearchTags) {
    ctx.patchState(
      produce(ctx.getState(), state => {
        state.filters.tags = tags;
      })
    );
    return ctx.dispatch(new NewsActions.Load());
  }
}
